import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';

export type SheetSnap = 'peek' | 'half' | 'full';

const ORDER: SheetSnap[] = ['peek', 'half', 'full'];
const FLICK_VELOCITY = 0.45; // px per ms

interface DragState {
  startY: number;
  startHeight: number;
  lastY: number;
  lastTime: number;
  velocity: number;
  moved: boolean;
}

export function useBottomSheet({
  enabled,
  containerRef,
  peekHeight = 132,
  initialSnap = 'half',
}: {
  enabled: boolean;
  containerRef: RefObject<HTMLElement | null>;
  peekHeight?: number;
  initialSnap?: SheetSnap;
}) {
  const [snap, setSnap] = useState<SheetSnap>(initialSnap);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const drag = useRef<DragState | null>(null);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    const el = containerRef.current;
    const measure = () => setContainerHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, containerRef]);

  const heights: Record<SheetSnap, number> = {
    peek: peekHeight,
    half: Math.max(peekHeight, Math.round(containerHeight * 0.5)),
    full: Math.max(peekHeight, containerHeight - 8),
  };

  const nearestSnap = (height: number): SheetSnap =>
    ORDER.reduce((best, key) => (Math.abs(heights[key] - height) < Math.abs(heights[best] - height) ? key : best), 'peek');

  const step = (direction: 1 | -1) => {
    setSnap((current) => ORDER[Math.min(ORDER.length - 1, Math.max(0, ORDER.indexOf(current) + direction))]);
  };

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      startY: event.clientY,
      startHeight: heights[snap],
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      moved: false,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state) return;
    const delta = state.startY - event.clientY;
    if (!state.moved && Math.abs(delta) < 6) return;
    state.moved = true;
    const elapsed = Math.max(1, event.timeStamp - state.lastTime);
    state.velocity = (state.lastY - event.clientY) / elapsed;
    state.lastY = event.clientY;
    state.lastTime = event.timeStamp;
    setDragHeight(Math.min(heights.full, Math.max(heights.peek, state.startHeight + delta)));
  };

  const onPointerUp = () => {
    const state = drag.current;
    drag.current = null;
    if (!state) return;
    if (!state.moved) {
      setSnap((current) => (current === 'full' ? 'peek' : ORDER[ORDER.indexOf(current) + 1]));
      return;
    }
    const height = dragHeight ?? state.startHeight;
    setDragHeight(null);
    if (state.velocity > FLICK_VELOCITY) {
      setSnap(ORDER.find((key) => heights[key] > height) ?? 'full');
      return;
    }
    if (state.velocity < -FLICK_VELOCITY) {
      setSnap([...ORDER].reverse().find((key) => heights[key] < height) ?? 'peek');
      return;
    }
    setSnap(nearestSnap(height));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      step(-1);
    }
  };

  return {
    snap,
    setSnap,
    height: dragHeight ?? heights[snap],
    dragging: dragHeight !== null,
    ready: containerHeight > 0,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
    },
  };
}
